import { useState, useEffect } from 'react'
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore'
import { db } from '../config/firebase'

export interface HeroImage {
  id?: string
  url: string
  order: number
}

const HERO_IMAGES_COLLECTION = 'heroImages'
const CACHE_KEY = 'forax_hero_images_cache'

// Placeholder por defecto (solo si no hay nada en caché ni Firebase)
const DEFAULT_PLACEHOLDER =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000'

export function useHeroImages() {
  // Cargar desde caché inmediatamente para carga instantánea
  const getCachedImages = (): HeroImage[] => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      return cached ? JSON.parse(cached) : []
    } catch {
      return []
    }
  }

  const [images, setImages] = useState<HeroImage[]>(getCachedImages())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadImages()
  }, [])

  const loadImages = async () => {
    try {
      setLoading(true)
      setError(null)

      const imagesRef = collection(db, HERO_IMAGES_COLLECTION)
      const q = query(imagesRef, orderBy('order', 'asc'))
      const querySnapshot = await getDocs(q)

      const loadedImages: HeroImage[] = []
      querySnapshot.forEach((doc) => {
        loadedImages.push({ id: doc.id, ...doc.data() } as HeroImage)
      })

      // Guardar en caché para próxima carga
      if (loadedImages.length > 0) {
        localStorage.setItem(CACHE_KEY, JSON.stringify(loadedImages))
        setImages(loadedImages)
      } else if (images.length === 0) {
        // Si no hay imágenes en Firebase ni en caché, usar placeholder
        const placeholderImage = [
          { id: 'placeholder', url: DEFAULT_PLACEHOLDER, order: 0 },
        ]
        setImages(placeholderImage)
      }

      setError(null)
    } catch (err: any) {
      console.error('Error loading hero images:', err)

      let errorMessage = 'Error al cargar imágenes.'

      if (err.message?.includes('Timeout')) {
        errorMessage = 'Firebase no responde. ¿Activaste Firestore Database?'
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Permisos denegados en Firestore.'
      } else {
        errorMessage = `Error: ${err.message || 'Problema de conexión'}`
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const addImage = async (url: string) => {
    if (images.length >= 4) return

    try {
      const newOrder = images.length
      const docRef = await addDoc(collection(db, HERO_IMAGES_COLLECTION), {
        url,
        order: newOrder,
      })
      const newImages = [...images, { id: docRef.id, url, order: newOrder }]
      setImages(newImages)
      localStorage.setItem(CACHE_KEY, JSON.stringify(newImages))
    } catch (err) {
      console.error('Error adding image:', err)
      alert('Error al subir imagen. Verifica tu conexión.')
    }
  }

  const deleteImage = async (id: string) => {
    try {
      await deleteDoc(doc(db, HERO_IMAGES_COLLECTION, id))
      const filtered = images.filter((img) => img.id !== id)

      // Reordenar imágenes restantes
      for (let i = 0; i < filtered.length; i++) {
        const img = filtered[i]
        if (img.id && img.order !== i) {
          await updateDoc(doc(db, HERO_IMAGES_COLLECTION, img.id), { order: i })
          filtered[i] = { ...img, order: i }
        }
      }

      setImages(filtered)
      localStorage.setItem(CACHE_KEY, JSON.stringify(filtered))
    } catch (err) {
      console.error('Error deleting image:', err)
      alert('Error al eliminar imagen. Verifica tu conexión.')
    }
  }

  const moveImage = async (id: string, direction: 'up' | 'down') => {
    const index = images.findIndex((img) => img.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === images.length - 1) return

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const newImages = [...images]

    // Intercambiar
    const temp = newImages[index]
    newImages[index] = newImages[targetIndex]
    newImages[targetIndex] = temp

    // Actualizar orden en Firebase
    try {
      if (newImages[index].id) {
        await updateDoc(doc(db, HERO_IMAGES_COLLECTION, newImages[index].id!), {
          order: index,
        })
      }
      if (newImages[targetIndex].id) {
        await updateDoc(
          doc(db, HERO_IMAGES_COLLECTION, newImages[targetIndex].id!),
          {
            order: targetIndex,
          },
        )
      }

      // Actualizar orden local
      newImages[index] = { ...newImages[index], order: index }
      newImages[targetIndex] = { ...newImages[targetIndex], order: targetIndex }

      setImages(newImages)
      localStorage.setItem(CACHE_KEY, JSON.stringify(newImages))
    } catch (err) {
      console.error('Error moving image:', err)
      alert('Error al reordenar imagen. Verifica tu conexión.')
    }
  }

  return {
    images,
    loading,
    error,
    addImage,
    deleteImage,
    moveImage,
    canAdd: images.length < 4,
  }
}
